import React, { useCallback, useDeferredValue, useMemo, useState } from 'react';
import { CSVLink } from 'react-csv';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { MinusCircle, Star } from 'lucide-react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { SimulationSettingsPanel } from '../../shared';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import {
  firstExpiryDays,
  formatDollars,
  formatPrice,
  maxHorizonDays,
  resolveHorizon,
  simulateStraddle,
} from '../../../utils/straddleCalculations';
import { formatLegLabel, matchesFilters } from '../../../utils/straddleMappings';
import { StraddleCostCell } from './StraddleSizingControls';
import StraddleRowTooltip, { TIP_IV, rowTip } from './StraddleRowTooltip';

const STICKY_COLUMNS = ['Ticker', 'Structure', 'Cost', 'Legs'];

const LEG_COLUMNS = (side) => [
  `${side} Strike`, `${side} Expiration`, `${side} DTE`, `${side} OTM %`,
  `${side} Premium`, `${side} IV`, `${side} Bid`, `${side} Ask`,
  `${side} Open Interest`, `${side} Volume`,
];

const GREEK_COLUMNS = [
  'Call Delta', 'Call Theta', 'Call Vega',
  'Put Delta', 'Put Theta', 'Put Vega',
  'Net Delta', 'Net Theta', 'Net Vega',
];

const SIMULATION_COLUMNS = [
  'Underlying Price', 'Simulated Underlying', 'Underlying Gain %', 'Days to Exit',
  'Simulated Call', 'Simulated Put', 'Simulated Value', 'Simulated P&L',
  'Return on Debit %', 'Ann. Return',
];

const POSITION_COLUMNS = [
  'Split', 'Expiry Relation', 'Call Contracts', 'Put Contracts', 'Call Spent', 'Put Spent',
  'Budget Left', 'Actual Split', 'Net Debit', 'Max Loss', 'Max Profit', 'Max Downside Profit',
  'Round-Trip Spread %', 'Quality', 'Call Tenor', 'Put Tenor',
];

const COLUMN_GROUPS = {
  Simulation: SIMULATION_COLUMNS,
  Position: POSITION_COLUMNS,
  Legs: [...LEG_COLUMNS('Call'), ...LEG_COLUMNS('Put')],
  Greeks: GREEK_COLUMNS,
  All: [
    ...SIMULATION_COLUMNS, ...POSITION_COLUMNS,
    ...LEG_COLUMNS('Call'), ...LEG_COLUMNS('Put'), ...GREEK_COLUMNS,
  ],
};

const COLUMN_TOOLTIPS = {
  Structure: 'Straddle: both legs on one strike. Strangle: two strikes.',
  Cost: 'What this position costs in total, and the most it can lose. With a budget set, the second line is how many calls and puts it buys.',
  'Budget Left': 'Your budget minus what this row spends. Negative means it spends more than the budget.',
  'Actual Split': 'How the money really divides between call and put, after rounding to whole contracts.',
  'Net Debit': 'What this position costs per share, counting every contract.',
  'Max Loss': 'Everything paid for this position, if both legs expire worthless.',
  'Max Profit': 'The call has no upper limit.',
  'Max Downside Profit': 'The most the puts in this position can make, if the stock goes to zero.',
  'Round-Trip Spread %': 'Cost of crossing both bid-ask spreads in and out, as a % of the debit.',
  'Simulated Value': 'What this whole position is projected to be worth at the exit date.',
  'Return on Debit %': 'Projected profit or loss as a % of what was paid.',
  'Ann. Return': 'Return on debit, annualized over the days to exit.',
};

const CURRENCY_COLUMNS = new Set([
  'Underlying Price', 'Simulated Underlying', 'Simulated Call', 'Simulated Put',
  'Net Debit', 'Call Strike', 'Put Strike', 'Call Premium', 'Put Premium',
  'Call Bid', 'Call Ask', 'Put Bid', 'Put Ask',
]);
const DOLLAR_COLUMNS = new Set([
  'Simulated Value', 'Simulated P&L', 'Max Loss', 'Max Downside Profit',
  'Call Spent', 'Put Spent', 'Budget Left',
]);
const PERCENT_COLUMNS = new Set([
  'Return on Debit %', 'Underlying Gain %', 'Ann. Return', 'Round-Trip Spread %',
  'Call IV', 'Put IV', 'Call OTM %', 'Put OTM %',
]);
const SIGNED_METRICS = new Set(['Return on Debit %', 'Simulated P&L', 'Ann. Return']);

const flatten = (row) => {
  const { call = {}, put = {} } = row._legs || {};
  const legFields = (side, leg) => ({
    [`${side} Premium`]: leg.premium,
    [`${side} IV`]: leg.iv != null ? leg.iv * 100 : null,
    [`${side} Bid`]: leg.bid,
    [`${side} Ask`]: leg.ask,
    [`${side} Open Interest`]: leg.open_interest,
    [`${side} Volume`]: leg.volume,
    [`${side} Delta`]: leg.delta,
    [`${side} Theta`]: leg.theta,
    [`${side} Vega`]: leg.vega,
  });
  const size = row._size;
  const callSpent = size ? size.call.spent : call.premium * 100;
  const putSpent = size ? size.put.spent : put.premium * 100;
  const callShare = Math.round((callSpent / (callSpent + putSpent)) * 100);
  return {
    ...row,
    'Max Profit': 'Unlimited',
    'Call Contracts': size ? size.call.contracts : 1,
    'Put Contracts': size ? size.put.contracts : 1,
    'Call Spent': callSpent,
    'Put Spent': putSpent,
    'Budget Left': size ? size.budget - size.spent : null,
    'Actual Split': `${callShare}% / ${100 - callShare}%`,
    ...legFields('Call', call),
    ...legFields('Put', put),
  };
};

const INITIAL_GAIN = 10;

/** Headers that need a shorter label to fit their fixed sticky slot. */
const COLUMN_LABELS = { Structure: 'Type' };

/** MobileScreener.css gives every button a 44px minimum, too big for this slot. */
const ROW_ACTION_SIZE = { minWidth: 16, minHeight: 16, padding: 0 };

const ROW_TIP_ID = 'straddle-detail-row-tooltip';

// Shared because toLocaleString builds a new formatter on every call.
const PLAIN_NUMBER = new Intl.NumberFormat('en-US', { maximumFractionDigits: 4 });

const IV_SIDES = { 'Call IV': 'call', 'Put IV': 'put' };

function formatValue(row, col) {
  if (col === 'Legs') {
    return (
      <div style={{ fontSize: 11, lineHeight: 1.3 }}>
        <div>{formatLegLabel(row, 'call')}</div>
        <div>{formatLegLabel(row, 'put')}</div>
      </div>
    );
  }
  if (col === 'Structure') {
    return (
      <div style={{ fontSize: 11, lineHeight: 1.3 }}>
        <div>{row.Structure}</div>
        <div className="text-gray-400">{row['Expiry Relation']}</div>
      </div>
    );
  }
  if (col === 'Cost') return <StraddleCostCell row={row} tooltipId={ROW_TIP_ID} />;
  const value = row[col];
  if (value === null || value === undefined || value === '') return '-';
  if (CURRENCY_COLUMNS.has(col)) return formatPrice(value);
  if (DOLLAR_COLUMNS.has(col)) return formatDollars(value);
  if (PERCENT_COLUMNS.has(col)) {
    const num = parseFloat(value);
    if (Number.isNaN(num)) return '-';
    if (Math.abs(num) >= 99999) return `${num.toExponential(1)}%`;
    if (Math.abs(num) >= 1000) return `${num.toFixed(0)}%`;
    return `${num.toFixed(2)}%`;
  }
  if (typeof value === 'number') return PLAIN_NUMBER.format(value);
  return value;
}

/** One row, memoized so a slider move skips the table on its urgent pass. */
const DetailRow = React.memo(function DetailRow({
  row, stripe, isFavorited, columns, isMobile, onToggleFavorite, onRemove,
}) {
  return (
    <tr className={`${stripe}${isFavorited ? ' table-row-favorited' : ''}`}>
      <td className="table-cell-sticky table-cell-sticky-0" style={{ padding: '2px' }}>
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            onClick={() => onToggleFavorite(row._key)}
            className="w-4 h-4 rounded-full border border-gray-600 hover:border-yellow-400 hover:bg-yellow-400/20 flex items-center justify-center transition-all duration-200 group"
            style={ROW_ACTION_SIZE}
            title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
          >
            <Star
              className={`w-3 h-3 ${isFavorited ? 'text-yellow-400' : 'text-gray-400 group-hover:text-yellow-400'}`}
              fill={isFavorited ? 'currentColor' : 'none'}
            />
          </button>
          <button
            type="button"
            onClick={() => onRemove(row._key)}
            className="w-4 h-4 rounded-full border border-gray-600 hover:border-[#ef4444] hover:bg-[#ef4444]/20 flex items-center justify-center transition-all duration-200 group"
            style={ROW_ACTION_SIZE}
            title="Remove from results"
          >
            <MinusCircle className="w-3 h-3 text-gray-400 group-hover:text-[#ef4444]" />
          </button>
        </div>
      </td>
      {columns.map((col) => {
        const position = STICKY_COLUMNS.indexOf(col) + 1;
        const stickyIndex = isMobile && position > 1 ? 0 : position;
        const numeric = parseFloat(row[col]);
        const colorClass = SIGNED_METRICS.has(col) && !Number.isNaN(numeric) && numeric !== 0
          ? (numeric > 0 ? 'table-cell-positive' : 'table-cell-negative')
          : '';
        return (
          <td
            key={col}
            className={`${stickyIndex ? `table-cell-sticky table-cell-sticky-${stickyIndex}` : 'table-cell'} table-cell-number ${colorClass}`}
            style={{
              ...(col === 'Max Profit' || col === 'Max Downside Profit' ? { color: '#1DB954' } : {}),
              ...(col === 'Max Loss' ? { color: '#ef4444' } : {}),
            }}
            {...(IV_SIDES[col] ? rowTip(ROW_TIP_ID, row, TIP_IV, IV_SIDES[col]) : {})}
          >
            {formatValue(row, col)}
          </td>
        );
      })}
    </tr>
  );
});

/**
 * Detail table for straddle results: every metric for each pair, repriced locally
 * at one horizon and one stock move.
 *
 * The horizon and its mode are shared with the heatmap, as are Move timing and IV
 * at exit, so switching views never changes the assumptions.
 */
function StraddleDetailTable({
  results,
  favoritedRows, setFavoritedRows,
  removedRows, setRemovedRows,
  showFavoritesOnly, setShowFavoritesOnly,
  sharedDaysToExit, setSharedDaysToExit,
  daysToExitMode, setDaysToExitMode,
  daysToExitPercent, setDaysToExitPercent,
  defaultDays,
  filters,
  moveTiming,
  ivShiftPct,
}) {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [selectedGroup, setSelectedGroup] = useState('Simulation');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  // Starts where the panel's slider does: it treats a default of 0 as unset.
  const [underlyingGain, setUnderlyingGain] = useState(INITIAL_GAIN);

  const isPercent = daysToExitMode === 'percent' && daysToExitPercent != null;
  const days = sharedDaysToExit ?? defaultDays;
  const maxDays = useMemo(() => maxHorizonDays(results), [results]);

  const deferredDays = useDeferredValue(days);
  const deferredPercent = useDeferredValue(daysToExitPercent);
  const deferredGain = useDeferredValue(underlyingGain);

  const processedData = useMemo(() => {
    const options = { moveTiming, ivShift: ivShiftPct / 100 };
    return (results || []).map((row) => {
      const horizon = resolveHorizon(row, isPercent ? null : deferredDays, isPercent ? deferredPercent : null);
      return flatten(simulateStraddle(row, horizon, deferredGain, options));
    });
  }, [results, isPercent, deferredDays, deferredPercent, deferredGain, moveTiming, ivShiftPct]);

  // Stars only matter here while filtering by them, so starring a row otherwise
  // doesn't reprice the whole table.
  const favoritesFilter = showFavoritesOnly ? favoritedRows : null;
  const filteredData = useMemo(() => processedData.filter((row) => (
    matchesFilters(row, filters)
    && !removedRows.has(row._key)
    && (!favoritesFilter || favoritesFilter.has(row._key))
  )), [processedData, filters, removedRows, favoritesFilter]);

  const sortedData = useMemo(() => {
    const value = (row, key) => (key === 'Legs' ? firstExpiryDays(row) : row[key]);
    return [...filteredData].sort((a, b) => {
      const favorite = (favoritedRows.has(a._key) ? 0 : 1) - (favoritedRows.has(b._key) ? 0 : 1);
      if (favorite !== 0) return favorite;
      if (!sortConfig.key) {
        return String(a.Ticker).localeCompare(String(b.Ticker))
          || firstExpiryDays(a) - firstExpiryDays(b)
          || a['Call Strike'] - b['Call Strike'];
      }
      const aVal = value(a, sortConfig.key);
      const bVal = value(b, sortConfig.key);
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      const aNum = parseFloat(aVal);
      const bNum = parseFloat(bVal);
      const result = !Number.isNaN(aNum) && !Number.isNaN(bNum)
        ? aNum - bNum
        : String(aVal).localeCompare(String(bVal));
      return sortConfig.direction === 'asc' ? result : -result;
    });
  }, [filteredData, sortConfig, favoritedRows]);

  const allColumns = useMemo(
    () => [...STICKY_COLUMNS, ...(COLUMN_GROUPS[selectedGroup] || [])],
    [selectedGroup]
  );

  const handleSort = (column) => {
    setSortConfig((prev) => (
      prev.key === column
        ? { key: column, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key: column, direction: 'asc' }
    ));
  };

  const getSortIcon = (column) => {
    if (sortConfig.key !== column) return <FaSort className="inline ml-1 opacity-50" />;
    return sortConfig.direction === 'asc'
      ? <FaSortUp className="inline ml-1" />
      : <FaSortDown className="inline ml-1" />;
  };

  const toggleFavorite = useCallback((key) => {
    setFavoritedRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, [setFavoritedRows]);

  const removeRow = useCallback((key) => {
    setRemovedRows((prev) => new Set([...prev, key]));
    setFavoritedRows((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, [setRemovedRows, setFavoritedRows]);

  const csvData = useMemo(() => sortedData.map((row) => {
    const out = {};
    allColumns.forEach((col) => {
      if (col === 'Legs') {
        out[col] = `${formatLegLabel(row, 'call')} / ${formatLegLabel(row, 'put')}`;
      } else {
        out[col] = row[col];
      }
    });
    return out;
  }), [sortedData, allColumns]);

  const stickyIndexOf = (col) => {
    const index = STICKY_COLUMNS.indexOf(col) + 1;
    return isMobile && index > 1 ? 0 : index;
  };

  const rowsByKey = useMemo(() => new Map(sortedData.map((row) => [row._key, row])), [sortedData]);

  if (!Array.isArray(results) || results.length === 0) return null;

  return (
    <div className="mt-6">
      <SimulationSettingsPanel
        maxDays={maxDays}
        onRefresh={(nextDays, gain) => {
          setSharedDaysToExit(nextDays);
          setUnderlyingGain(gain);
        }}
        onReset={() => {
          setDaysToExitMode('fixed');
          setDaysToExitPercent(null);
          setSharedDaysToExit(defaultDays);
          setUnderlyingGain(INITIAL_GAIN);
        }}
        isActive
        defaultDays={Math.min(defaultDays, maxDays)}
        defaultGain={INITIAL_GAIN}
        contractCount={sortedData.length}
        externalDays={sharedDaysToExit}
        daysToExitMode={daysToExitMode}
        daysToExitPercent={daysToExitPercent}
        onModeChange={setDaysToExitMode}
        onPercentChange={(pct) => {
          setDaysToExitMode('percent');
          setDaysToExitPercent(pct);
        }}
      />

      <div className="flex flex-wrap gap-2 items-center justify-between mb-3">
        <div className="flex flex-wrap gap-2">
          {Object.keys(COLUMN_GROUPS).map((group) => (
            <button
              key={group}
              type="button"
              onClick={() => setSelectedGroup(group)}
              className={`tab-button ${selectedGroup === group ? 'tab-selected' : 'tab-unselected'}`}
            >
              {group}
            </button>
          ))}
        </div>
        <div className="flex gap-2 items-center">
          <button
            type="button"
            onClick={() => setShowFavoritesOnly((p) => !p)}
            disabled={favoritedRows.size === 0 && !showFavoritesOnly}
            className={`flex items-center gap-1.5 font-medium text-sm py-1 px-3 rounded transition-all duration-200 border ${
              showFavoritesOnly
                ? 'bg-yellow-400/20 text-yellow-400 border-yellow-400'
                : favoritedRows.size === 0
                  ? 'bg-[#1f2937] text-[#555] border-[#333] cursor-not-allowed'
                  : 'bg-[#1f2937] text-[#b0b0b0] border-[#333] hover:bg-yellow-400/10 hover:text-yellow-400 hover:border-yellow-400'
            }`}
          >
            <Star className="w-3.5 h-3.5" fill={showFavoritesOnly || favoritedRows.size > 0 ? 'currentColor' : 'none'} />
            Favorites{favoritedRows.size > 0 ? ` (${favoritedRows.size})` : ''}
          </button>
          <CSVLink data={csvData} filename="straddle_detail_optionsLeveling.csv" className="btn-export-primary">
            Export CSV
          </CSVLink>
        </div>
      </div>

      <div className="table-container">
        <div className="table-scroll-container">
          <table className="table">
            <thead className="table-header">
              <tr>
                <th className="table-header-cell-sticky table-header-cell-sticky-0" />
                {allColumns.map((col) => {
                  const stickyIndex = stickyIndexOf(col);
                  const tip = COLUMN_TOOLTIPS[col];
                  return (
                    <th
                      key={col}
                      onClick={() => handleSort(col)}
                      className={stickyIndex
                        ? `table-header-cell-sticky table-header-cell-sticky-${stickyIndex}`
                        : 'table-header-cell'}
                      {...(tip ? { 'data-tooltip-id': 'straddle-detail-tooltip', 'data-tooltip-content': tip } : {})}
                    >
                      <div className="flex flex-col items-center justify-center">
                        <span className="text-center">{COLUMN_LABELS[col] || col}</span>
                        <span className="mt-1">{getSortIcon(col)}</span>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {sortedData.map((row, idx) => (
                <DetailRow
                  key={row._key}
                  row={row}
                  stripe={idx % 2 === 0 ? 'table-row-even' : 'table-row-odd'}
                  isFavorited={favoritedRows.has(row._key)}
                  columns={allColumns}
                  isMobile={isMobile}
                  onToggleFavorite={toggleFavorite}
                  onRemove={removeRow}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-sm text-gray-400 mt-3">
        Showing {sortedData.length} of {results.length} {results[0]?._size?.showSplit ? 'rows' : 'straddles'}
      </div>

      <ReactTooltip
        id="straddle-detail-tooltip"
        place="top"
        delayShow={300}
        style={{
          backgroundColor: '#111827', color: '#fff', borderRadius: 8, padding: '8px 12px',
          fontSize: 13, maxWidth: 320, lineHeight: 1.4, zIndex: 1200, whiteSpace: 'pre-line',
          boxShadow: '0 4px 12px rgba(0,0,0,0.5)', border: '1px solid #374151',
        }}
      />
      <StraddleRowTooltip
        id={ROW_TIP_ID}
        rowsByKey={rowsByKey}
        style={{
          backgroundColor: '#111827', color: '#fff', borderRadius: 8, padding: '8px 12px',
          fontSize: 13, maxWidth: 320, lineHeight: 1.4, zIndex: 1200, whiteSpace: 'pre-line',
          boxShadow: '0 4px 12px rgba(0,0,0,0.5)', border: '1px solid #374151',
        }}
      />
    </div>
  );
}

export default StraddleDetailTable;
